import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { nav, site } from '../mock';

function isChildActive(item, pathname) {
  if (!item?.children) return false;
  return item.children.some((c) => c.to === pathname);
}

function NavItem({ item, pathname }) {
  const [open, setOpen] = useState(false);
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const active = pathname === item.to || isChildActive(item, pathname);

  const baseCls = `text-[13.5px] font-medium tracking-wide transition-colors pb-1 ${
    active ? 'text-[#133844] border-b border-[#133844]' : 'text-[#232830] hover:text-[#133844]'
  }`;

  if (!hasChildren) {
    return (
      <NavLink to={item.to} end={item.to === '/'} className={baseCls}>
        {item.label}
      </NavLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink
        to={item.to}
        end={item.to === '/'}
        className={`${baseCls} inline-flex items-center gap-1`}
      >
        {item.label}
        <ChevronDown className="h-3.5 w-3.5" />
      </NavLink>

      {open && (
        <div className="absolute left-0 top-full pt-3 z-50" data-testid={`nav-dropdown-${item.label.toLowerCase()}`}>
          <div className="min-w-[240px] bg-white border border-[#ECEEF1] shadow-md py-2">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-2 text-[13px] whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-[#D1F9F1] text-[#133844] font-semibold'
                      : 'text-[#232830] hover:bg-[#F7F8FA] hover:text-[#133844]'
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 left-0 right-0 z-50 bg-white transition-shadow duration-200 ${
        scrolled ? 'border-b border-[#B5BDC8] shadow-[0_1px_0_rgba(0,0,0,0.02)]' : 'border-b border-[#ECEEF1]'
      }`}
    >
      <div className="container-x flex items-center justify-between py-4">
        <Link to="/" className="flex flex-col leading-tight">
          <div className="heading-serif text-[#232830] text-[20px] md:text-[22px]">{site.name}</div>
          <div className="text-[9.5px] tracking-[0.22em] text-[#546072] uppercase mt-0.5 hidden md:block">{site.tagline}</div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {nav.map((n) => (
            <NavItem key={n.to + n.label} item={n} pathname={location.pathname} />
          ))}
        </nav>

        <button
          className="lg:hidden text-[#232830] p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#ECEEF1]">
          <div className="container-x flex flex-col py-3">
            {nav.map((n) => (
              <React.Fragment key={n.to + n.label}>
                <NavLink
                  to={n.to}
                  end={n.to === '/'}
                  className={({ isActive }) =>
                    `py-3 border-b border-[#ECEEF1] text-[14px] ${isActive ? 'text-[#133844] font-semibold' : 'text-[#232830]'}`
                  }
                >
                  {n.label}
                </NavLink>
                {Array.isArray(n.children) && n.children.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    className={({ isActive }) =>
                      `pl-4 py-2.5 border-b border-[#ECEEF1] text-[13px] ${isActive ? 'text-[#133844] font-semibold' : 'text-[#546072]'}`
                    }
                  >
                    · {c.label}
                  </NavLink>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
